import classNames from "classnames";
import { useObjectMap } from "@ascendnow/shared";
import { useMemo, useRef, useState } from "react";
import AscButton from "../AscButton/AscButton";
import AscCorrectAnswersCard from "../AscCards/AscCorrectAnswersCard/AscCorrectAnswersCard";
import {
  CheckMarkIcon,
  ClassicChevronLeftIcon,
  ClassicChevronRightIcon,
} from "../AscIcons";
import styles from "./AscQuizDragAndDropSetActivity.module.scss";
import {
  QuizDragAndDropQuestion,
  QuizDragAndDropQuestionAnswer,
} from "@ascendnow/shared";
import AscQuizDragAndDropQuestion from "./AscQuizDragAndDropQuestion/AscQuizDragAndDropQuestion";

export interface AscQuizDragAndDropSetActivityProps {
  className?: string;
  questions: QuizDragAndDropQuestion[];
  loading?: boolean;
  attemptsCompleted?: boolean;
  onCheckAnswerForQuestion?: (
    questionId: string,
    answers: QuizDragAndDropQuestionAnswer[]
  ) => Promise<QuizDragAndDropQuestionAnswer[] | undefined>;
  onSubmitAnswers?: (
    answers: {
      questionId: string;
      answers: QuizDragAndDropQuestionAnswer[];
    }[]
  ) => Promise<
    | {
        questionId: string;
        answers: QuizDragAndDropQuestionAnswer[];
      }[]
    | undefined
  >;
  disabled?: boolean;
  defaultTitle?: string;
}

export default function AscQuizDragAndDropSetActivity({
  className,
  questions,
  loading = false,
  attemptsCompleted,
  onCheckAnswerForQuestion,
  onSubmitAnswers,
  disabled = false,
  defaultTitle = "Drag & Drop",
}: AscQuizDragAndDropSetActivityProps) {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOnLastQuestion = activeQuestionIndex === questions?.length - 1;
  const [answerLoading, setAnswerLoading] = useState<boolean>(false);
  const [allAnswerLoading, setAllAnswersLoading] = useState<boolean>(false);
  const [answers, answersActions] = useObjectMap<{
    answers: QuizDragAndDropQuestionAnswer[];
    showCorrectAnswer?: boolean;
  }>();
  const activeQuestionId = useMemo(
    () => questions[activeQuestionIndex]?.id,
    [activeQuestionIndex, questions]
  );

  function onPrevious() {
    if (activeQuestionIndex === 0) {
      return;
    }
    setActiveQuestionIndex(activeQuestionIndex - 1);
  }

  function onNext() {
    if (activeQuestionIndex >= questions?.length - 1) {
      return;
    }
    setActiveQuestionIndex(activeQuestionIndex + 1);
  }

  function onNavigateWithKeyboard(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowLeft") {
      onPrevious();
      return;
    }
    if (event.key === "ArrowRight") {
      onNext();
      return;
    }
    if (
      event.key === "Enter" &&
      (event.metaKey || event.ctrlKey || event.shiftKey)
    ) {
      handleSubmitAnswers();
      return;
    }
    if (event.key === "Enter") {
      handleCheckAnswer();
      return;
    }
  }

  function handleAnswerChange(
    questionId: string,
    answers: QuizDragAndDropQuestionAnswer[]
  ) {
    answersActions.upsert(questionId, {
      answers,
    });
  }

  async function handleCheckAnswer() {
    const questionId = activeQuestionId;
    const answersForQuestion = answers?.[questionId]?.answers;
    const showCorrectAnswer = answers?.[questionId]?.showCorrectAnswer;
    const correct =
      answersForQuestion?.every((a) => a?.correct) &&
      answersForQuestion?.length ===
        questions?.[activeQuestionIndex]?.questions?.length;

    if (attemptsCompleted || correct || disabled) {
      return;
    }
    if (showCorrectAnswer) {
      answersActions.remove(questionId);
      return;
    }
    if (!questionId || !answersForQuestion?.length) {
      return;
    }
    setAnswerLoading(true);
    const correctAnswer = await onCheckAnswerForQuestion?.(
      questionId,
      answersForQuestion
    );
    if (!correctAnswer) {
      setAnswerLoading(false);
      return;
    }
    answersActions.update(questionId, {
      answers: correctAnswer,
      showCorrectAnswer: true,
    });
    setAnswerLoading(false);
  }

  async function handleSubmitAnswers() {
    if (answersActions.length() !== questions.length || allAnswerLoading) {
      return;
    }

    setAllAnswersLoading(true);
    const correctAnswers = await onSubmitAnswers?.(
      answersActions.map(([k, a]) => ({
        questionId: k,
        answers: a?.answers,
      }))
    );
    if (correctAnswers) {
      const _correctAnswers: [
        string,
        {
          answers: QuizDragAndDropQuestionAnswer[];
          showCorrectAnswers?: boolean;
        }
      ][] = correctAnswers?.map((ca) => [
        ca?.questionId,
        {
          answers: ca?.answers,
          showCorrectAnswer: true,
        },
      ]);
      answersActions.setAll(_correctAnswers);
    }
    setAllAnswersLoading(false);
  }

  const questionsLength =
    questions?.[activeQuestionIndex]?.questions.length || 0;
  const answersLength = answers?.[activeQuestionId]?.answers?.length || 0;
  const inCorrectAnswersLength =
    answers?.[activeQuestionId]?.answers?.filter((el) => !el?.correct).length ||
    0;

  return (
    <div
      data-testid="asc-quiz-question-set-activity"
      className={classNames(styles.ground, className)}
      onClick={() => {
        inputRef?.current?.focus();
      }}
    >
      <div className={styles.navigation}>
        <input
          className={styles.hiddenNavigationInput}
          ref={inputRef}
          autoFocus
          onKeyDown={onNavigateWithKeyboard}
        />
        <AscButton
          variant="lightGray"
          className={classNames(styles.previousButton, {
            [styles.hidden]: activeQuestionIndex === 0,
          })}
          icon={<ClassicChevronLeftIcon />}
          disabled={activeQuestionIndex === 0}
          onClick={onPrevious}
          squarishIcon
        />

        <div className={styles.title}>
          {questions[activeQuestionIndex]?.title || defaultTitle}
        </div>

        <AscButton
          variant="lightGray"
          className={classNames(styles.nextButton)}
          icon={
            !isOnLastQuestion ? <ClassicChevronRightIcon /> : <CheckMarkIcon />
          }
          loading={allAnswerLoading}
          disabled={
            isOnLastQuestion && answersActions.length() !== questions.length
          }
          squarishIcon
          onClick={!isOnLastQuestion ? onNext : handleSubmitAnswers}
        />
      </div>
      <div className={styles.questionWrapper}>
        <AscQuizDragAndDropQuestion
          className={styles.questionItem}
          question={questions[activeQuestionIndex]}
          onAnswerChange={handleAnswerChange}
          answers={answers?.[activeQuestionId]?.answers || []}
          attemptsCompleted={attemptsCompleted}
          disabled={
            allAnswerLoading ||
            disabled ||
            attemptsCompleted ||
            answers?.[activeQuestionId]?.showCorrectAnswer
          }
        />
      </div>

      <AscCorrectAnswersCard
        type="secondary"
        className={styles.answersCard}
        loading={answerLoading || allAnswerLoading || loading}
        disabled={disabled || !answersLength}
        attempted={
          !!answers?.[activeQuestionId]?.answers?.some?.(
            (a) => a?.correct !== undefined
          )
        }
        attemptsCompleted={attemptsCompleted}
        onAttempt={handleCheckAnswer}
        correct={
          answers?.[activeQuestionId]?.answers?.some(
            (a) => a?.correct !== undefined
          )
            ? answers?.[activeQuestionId]?.answers?.every((a) => a?.correct) &&
              answersLength === questionsLength
            : undefined
        }
        incorrectText={`Incorrect ${
          inCorrectAnswersLength +
          (questionsLength - answersLength) +
          "/" +
          questionsLength
        }`}
      />
    </div>
  );
}
